import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import GET_OLA_LISTING from "../../../graphql/queries/ola/getOlaListing";
import GET_COUNTRIES from "../../../graphql/queries/registration/getCountries";
import GET_LA_COUNT from "../../../graphql/queries/ola/laCount";

import CustomTable from "../../../components/Table";
import TableSkeleton from "../../../components/Table/components/TableSkeleton";
import Filters from "../../../components/Filters";

import { useEffect, useState } from "react";
import { useOlaFilters } from "./hooks/useOlaFilters";

import { useTranslation } from "react-i18next";

const LearningAgreementList = ({ typeOfStudent, userHeiID }) => {
  const academicFilter = () => new Date().getMonth() < 6 ?  (new Date().getFullYear() - 1) + "/" + new Date().getFullYear() : new Date().getFullYear() + "/" + (new Date().getFullYear() + 1);
  const initialState = {
    academicYear: academicFilter(),
    createdByStudent: "",
    country: [],
    erasmusCode: "",
    studentLastName: "",
    studentFirstName: "",
    organizationalUnit: "",
    status: "",
    iscedCodeLA: "",
    sortBy: localStorage.getItem('sortByLAs') !== null ? JSON.parse(localStorage.getItem('sortByLAs')) : "changed_descending",
    selectedFiltersList: localStorage.getItem('selectedFiltersLAs') !== null ? JSON.parse(localStorage.getItem('selectedFiltersLAs')) : {
      erasmusCode: "",
      status: "",
      createdByStudent: "",
      country: [],
      academicYear: academicFilter(),
      studentFirstName: "",
      studentLastName: "",
      organizationalUnit: "",
      iscedCodeLA: "",
    },
  };

  const filters = useOlaFilters(initialState);
  const {
    state,
    setSelectedFiltersList,
    setOrganizationalUnit,
    setExpandFilters,
    setIscedCodeLA,
    setSortBy,
    updateMultipleValues,
  } = filters;
  const {
    academicYear,
    createdByStudent,
    country,
    erasmusCode,
    studentLastName,
    studentFirstName,
    organizationalUnit,
    status,
    selectedFiltersList,
    iscedCodeLA,
    sortBy,
    expandFilters,
  } = state;
  const [page, setPage] = useState(localStorage.getItem('pageLAs') !== null ? parseInt(JSON.parse(localStorage.getItem('pageLAs'))) : 0);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const getCreatedByFilter = () => {
    if (createdByStudent === "Created by Student") {
      return true;
    } else if (createdByStudent === "Created by Institution") {
      return false;
    } else {
      return undefined;
    }
  };

  const getCreatedByFilterForLaCount = () => {
    if (createdByStudent === "Created by Student") {
      return "student";
    } else if (createdByStudent === "Created by Institution") {
      return "dashboard";
    } else {
      return undefined;
    }
  };

  const {
    loading: olaListingLoading,
    error: olaListingError,
    data: olaListingData,
    refetch: olaListingRefetch,
  } = useQuery(GET_OLA_LISTING, {
    variables: {
      typeOfStudent: typeOfStudent,
      page: page,
      sortBy: sortBy,
      optionalOLAFilters: {
        academicYear: academicYear,
        createdByStudent: getCreatedByFilter(),
        country: country,
        erasmusCode: erasmusCode,
        studentLastName: studentLastName,
        studentFirstName: studentFirstName,
        faculty: organizationalUnit,
        status: status,
        iscedId: iscedCodeLA
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: laCountLoading,
    error: laCountError,
    data: laCount,
    refetch: laCountRefetch,
  } = useQuery(GET_LA_COUNT, {
    variables: {
      homeHeiID: typeOfStudent === 1 ? userHeiID : "",
      hostHeiID: typeOfStudent === 0 ? userHeiID : "",
      academicYear: academicYear,
      status: status,
      studentFirstName: studentFirstName,
      studentLastName: studentLastName,
      erasmusCode: erasmusCode,
      createdBy: getCreatedByFilterForLaCount(),
      country: country,
      faculty: organizationalUnit,
      iscedId: iscedCodeLA,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: loadingCountries,
    error: errorCountries,
    data: dataCountries,
  } = useQuery(GET_COUNTRIES);

  const handleExpand = (value) => {
    setExpandFilters(value);
  };

  useEffect(() => {
    const data = localStorage.getItem('selectedFiltersLAs');

    if (data) {
      if (!Object.values(JSON.parse(data) || {})?.every(x => x === '')) {
        setSelectedFiltersList(JSON.parse(data));
        olaListingRefetch();
        laCountRefetch();
      }
      
    }
  }, []);

  useEffect(() => {
    page !== null && localStorage.setItem('pageLAs', JSON.stringify(page));
  }, [page]);

  useEffect(() => {
    localStorage.setItem('selectedFiltersLAs', JSON.stringify(selectedFiltersList));
  }, [selectedFiltersList]);

  useEffect(() => {
    sortBy !== null && localStorage.setItem('sortByLAs', JSON.stringify(sortBy));
  }, [sortBy]);

  useEffect(() => {
    updateMultipleValues(selectedFiltersList);
    olaListingRefetch();
    laCountRefetch();
  }, [
    selectedFiltersList,
    updateMultipleValues,
    olaListingRefetch,
    laCountRefetch,
  ]);

  const handleRemoveFilter = (filter, value) => {
    if (filter === "country") {
      let temp = Object.assign({}, selectedFiltersList);
      temp[filter] = temp[filter].filter((i) => i !== value);
      setSelectedFiltersList(temp);
    } else {
      let temp = Object.assign({}, selectedFiltersList);
      temp[filter] = "";
      setSelectedFiltersList(temp);
    }
  };
  const formatDate = (string) => {
    var options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(string).toLocaleDateString("en-UK",options);
  }

  if (olaListingLoading || loadingCountries || laCountLoading) {
    return (
      <TableSkeleton
        columnsPerFieldMap={{
          id_:"col-1",
          first_name: "col-2",
          last_name: "col-2",
          planned_period: "col-2",
          status: "col-2",
          sending_institution: "col-3",
        }}
      />
    );
  }

  if (olaListingError || errorCountries || laCountError)
    return (
      <div className="text-center text-danger mx-auto">{t('errorMessage')}</div>
    );

  return (
    <main className="h-auto text-center mt-4">
      <CustomTable
        data={typeOfStudent === 1 ? olaListingData.learningAgreements.learningAgreementList.map(
          ({
            olaID,
            title,
            field_student_firstname,
            field_student_lastname,
            field_la_status,
            receiving_institution,
            field_planned_period_from,
            field_planned_period_to,
          }) => ({
            id: olaID,
            id_: title.replace("LA-",""),
            first_name: field_student_firstname,
            last_name: field_student_lastname,
            planned_period: formatDate(field_planned_period_from)+" - "+formatDate(field_planned_period_to),
            status: field_la_status,
            receiving_institution: <div dangerouslySetInnerHTML={{__html: receiving_institution}}/>,
          })
        ) : olaListingData.learningAgreements.learningAgreementList.map(
          ({
            olaID,
            title,
            field_student_firstname,
            field_student_lastname,
            field_la_status,
            sending_institution,
            field_planned_period_from,
            field_planned_period_to,
          }) => ({
            id: olaID,
            id_: title.replace("LA-",""),
            first_name: field_student_firstname,
            last_name: field_student_lastname,
            planned_period: formatDate(field_planned_period_from)+" - "+formatDate(field_planned_period_to),
            status: field_la_status,
            sending_institution: <div dangerouslySetInnerHTML={{__html: sending_institution}}/>,
          })
        )}
        page={page}
        setPage={(p) => setPage(p)}
        type={"ola-typeOfStudent-" + typeOfStudent + "-"}
        expandFilters={expandFilters}
        setExpandFilters={(e) => setExpandFilters(e)}
        filters={selectedFiltersList}
        setFilters={(filter, value) => {handleRemoveFilter(filter, value); setPage(0);}}
        sortBy={sortBy}
        setSortBy={() => setSortBy("")}
        columnsPerFieldMap={typeOfStudent === 1 ? {
          id_:"col-1",
          first_name: "col-2",
          last_name: "col-2",
          planned_period: "col-2",
          status: "col-2",
          receiving_institution: "col-3",
        } : {
          id_:"col-1",
          first_name: "col-2",
          last_name: "col-2",
          planned_period: "col-2",
          status: "col-2",
          sending_institution: "col-3",
        }}
        pageLimit={30}
        totalCount={laCount?.laCount}
        hasPagination={true}
        hasFilters={true}
        onClickHandlers={olaListingData.learningAgreements.learningAgreementList.map(
          ({ olaID }) =>
            (e) => {
              navigate(
                typeOfStudent === 1
                  ? "/ola/outgoing/" + olaID
                  : "/ola/incoming/" + olaID
              );
            }
        )}
      />
      <Filters
        expand={expandFilters}
        handleExpand={handleExpand}
        selectedFiltersList={selectedFiltersList}
        setSelectedFiltersList={(e) => {setSelectedFiltersList(e); setPage(0);}}
        sortBy={sortBy}
        setSortBy={(e) => setSortBy(e)}
        countries={dataCountries?.countries}
        type={"ola"}
      />
    </main>
  );
};

export default LearningAgreementList;
